import { LogOut, MessageSquare, Settings, Shield, Printer } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ShippingLabelsHistory } from "./ShippingLabelsHistory";

interface UserMenuProps {
  userEmail: string;
  userName?: string;
}

const UserMenu = ({ userEmail, userName }: UserMenuProps) => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLabelsDialog, setShowLabelsDialog] = useState(false);

  useEffect(() => {
    checkAdminRole();
  }, []);

  const checkAdminRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase.rpc('has_role', {
        _user_id: user.id,
        _role: 'admin'
      });

      setIsAdmin(data === true);
    } catch (error) {
      console.error('Error checking admin role:', error);
    }
  };

  const getInitials = () => {
    if (userName && userName !== userEmail) {
      return userName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return userEmail.charAt(0).toUpperCase();
  };

  const handleLogout = async () => {
    try {
      console.log('[UserMenu] Logging out...');
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('[UserMenu] Logout error:', error);
      }
      console.log('[UserMenu] Logout complete, navigating to /auth');
      navigate("/auth", { replace: true });
    } catch (error) {
      console.error('[UserMenu] Logout exception:', error);
      // Force navigate anyway
      navigate("/auth", { replace: true });
    }
  };

  const handleFeedback = () => {
    window.open("https://endless-storage.canny.io/feature-requests", "_blank");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <Avatar className="h-8 w-8 cursor-pointer">
            <AvatarFallback className="bg-primary text-primary-foreground text-sm">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm text-muted-foreground hidden md:inline">{userEmail}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">{userName || "My Account"}</p>
            <p className="text-xs text-muted-foreground">{userEmail}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isAdmin && (
          <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer">
            <Shield className="mr-2 h-4 w-4" />
            Admin Panel
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer">
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setShowLabelsDialog(true)} className="cursor-pointer">
          <Printer className="mr-2 h-4 w-4" />
          Shipping Labels
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleFeedback} className="cursor-pointer">
          <MessageSquare className="mr-2 h-4 w-4" />
          Send Feedback
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
      <ShippingLabelsHistory 
        isOpen={showLabelsDialog} 
        onOpenChange={setShowLabelsDialog} 
      />
    </DropdownMenu>
  );
};

export default UserMenu;
